import { JSX, Key, useEffect, useState } from "react";
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@nextui-org/react";
import {
  LeftArrowIcon,
  RightArrowIcon,
  SearchIcon,
} from "@/assets/icons/admin";
import CustomDropdown from "../admin/customdropdown";
import { UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse, HttpStatusCode } from "axios";
import { convertUTCToLocal } from "@/utils/dex";

const rowsPerPageOptions = ["25", "50", "100", "250", "500", "1000"];

interface DatatableProps {
  columns: { key: string; label: string; align?: string }[];
  renderCell: (data: any[], item: any, columnKey: Key) => JSX.Element;
  query?: (args: any) => UseQueryResult<AxiosResponse<any>, unknown>;
  staticData?: any;
  customFilters?: React.ReactNode[];
  searchFields: { label: string; value: string }[];
  customQueryFilter?: object;
  triggerRefetch?: boolean;
  onRefetch?: () => void;
  customActions?: React.ReactNode;
  hideSearchKey?: boolean;
  hideFilter?: boolean;
  tableSize?: number;
  hidePagination?: boolean;
  defaultRowsPerPage?: string;
  defaultSort?: string;
  defaultOrder?: string;
  customSummaryComponent?: (data: any) => React.ReactNode;
  autoFetch?: boolean;
  updatedTimeData?: boolean;
  customJson?: boolean;
  timeline?: string;
}

export default function Datatable({
  columns,
  renderCell,
  query,
  staticData,
  customFilters,
  searchFields,
  customQueryFilter,
  triggerRefetch,
  customActions,
  onRefetch,
  hideSearchKey,
  hideFilter,
  hidePagination,
  tableSize = 0,
  defaultRowsPerPage = "25",
  defaultSort = "createdAt",
  defaultOrder = "desc",
  customSummaryComponent,
  autoFetch,
  updatedTimeData,
  customJson,
  timeline,
}: DatatableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [sortOrder, setSortOrder] = useState(defaultOrder);
  const [sort, setSort] = useState(defaultSort);
  const [searchKey, setSearchKey] = useState(
    searchFields.length > 0 ? searchFields[0].value : "",
  );
  const [searchValue, setSearchValue] = useState("");
  const isUsingQuery = !!query;

  // const {
  //   data: tableRows,
  //   refetch,
  //   isFetching,
  //   isError,
  //   error,
  // } = query({
  //   page: currentPage,
  //   pageSize: parseInt(rowsPerPage),
  //   sort: sort,
  //   order: sortOrder,
  //   searchKey,
  //   searchValue,
  //   ...customQueryFilter,
  // });

  const {
    data: tableRows,
    refetch,
    isFetching,
    isError,
    error,
  } = isUsingQuery
    ? query({
        page: currentPage,
        pageSize: parseInt(rowsPerPage),
        sort,
        order: sortOrder,
        searchKey,
        searchValue,
        ...customQueryFilter,
      })
    : {
        data: staticData,
        refetch: () => {},
        isFetching: false,
        isError: false,
        error: null,
      };

  useEffect(() => {
    refetch();
  }, [
    rowsPerPage,
    currentPage,
    sortOrder,
    sort,
    refetch,
    autoFetch && customQueryFilter,
  ]);

  if (triggerRefetch) {
    if (onRefetch) {
      onRefetch();
    }
    refetch();
  }

  let data;

  if (customJson) {
    data = tableRows?.data?.data.records || [];
  } else {
    data = tableRows?.data?.data || [];
  }

  let lastUpdatedDate = tableRows?.data?.data.lastUpdated;

  const pagination = tableRows?.data?.pagination;

  if (isError) {
    if ((error as any).status === HttpStatusCode.Forbidden) {
      return (
        <div className="m-auto flex h-[24rem] w-full flex-col items-center justify-center gap-4">
          <h2 className="text-4xl">403 Forbiddden</h2>
          <p>You are not allowed to access this page</p>
        </div>
      );
    }
    data = [];
  }

  const handleChangeRowsPerPage = (e: any) => {
    setRowsPerPage(e.target.value);
    setCurrentPage(1);
  };

  const handleClickNextPage = () => {
    if (currentPage >= pagination?.totalPages) {
      return;
    }
    setCurrentPage((prevPage) => prevPage + 1);
    refetch();
  };

  const handleClickPrevPage = () => {
    if (currentPage == 1) {
      return;
    }
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
    refetch();
  };

  const sortHandler = async (descriptor: any) => {
    const newSortOrder = descriptor.direction === "ascending" ? "asc" : "desc";
    setSortOrder(newSortOrder);
    setSort(descriptor.column);
  };

  const totalRows = pagination?.totalResults || 0;
  const startRow = (currentPage - 1) * parseInt(rowsPerPage) + 1;
  const endRow = startRow + data.length - 1;
  const rowsRange = `${startRow}-${endRow} out of ${totalRows}`;

  return (
    <>
      {/* Filters */}
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-row items-end justify-between">
          {!hideFilter && (
            <div className="mb-6 mt-5 flex w-full flex-wrap items-start gap-4 md:mb-0 md:flex-nowrap">
              {customFilters}

              {!hideSearchKey && (
                <div className="w-[170px] sm:w-2/5">
                  <CustomDropdown
                    options={searchFields}
                    placeholder={searchFields[0]?.label || ""}
                    onSelect={(e) => {
                      setSearchKey(e?.value || "");
                    }}
                    value={searchKey ?? ""}
                  />
                </div>
              )}
              <Input
                className="input-bg w-[170px] sm:w-2/4"
                isClearable
                classNames={{
                  input: [
                    "mr-0",
                    "text-white/90 dark:text-white/90",
                    "placeholder:text-white/40 dark:placeholder:text-white/60 ml-3",
                  ],
                  inputWrapper: [
                    "shadow-xl",
                    "!cursor-text",
                    "bg-white-5",
                    "text-[11px]",
                  ],
                }}
                placeholder="Search Keyword"
                startContent={<SearchIcon />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <Button
                className="admin-common-button-gradient font-ubuntu w-[170px] rounded-md text-base font-bold"
                onClick={() => {
                  refetch();
                }}
              >
                Apply
              </Button>
            </div>
          )}

          <div className="flex w-1/4 flex-row justify-end gap-2">
            {customActions}
          </div>
        </div>
      </div>
      {updatedTimeData && (
        <h1 className="mt-4 w-full text-end font-openSans text-[12px] font-normal italic text-[#A6A6A6]">
          {`Showing the last ${timeline === "weekly" ? "7 days" : timeline === "daily" ? "24 hours" : "30 days"}.`}
          {" Last updated on "}
          {convertUTCToLocal(lastUpdatedDate)}
        </h1>
      )}

      {customSummaryComponent && customSummaryComponent(tableRows?.data)}

      {/* Table Body */}
      <div className="sm:mt-5">
        <div
          className={`w-full overflow-x-auto ${tableSize !== 0 ? "overflow-y-auto" : ""}`}
          style={{ height: tableSize !== 0 ? `${tableSize}px` : "auto" }}
        >
          <Table
            removeWrapper
            className="table-striped table-striped-even table"
            sortDescriptor={{
              column: sort,
              direction: sortOrder === "asc" ? "ascending" : "descending",
            }}
            onSortChange={sortHandler}
          >
            <TableHeader className="flex rounded-none" columns={columns}>
              {(column: any) => (
                <TableColumn
                  key={column.key}
                  allowsSorting={column.sortable}
                  className={`${column.align ? "text-" + column.align : ""}`}
                >
                  {column.label}
                </TableColumn>
              )}
            </TableHeader>
            <TableBody
              items={data}
              emptyContent={"No rows to display."}
              loadingContent={"Loading data..."}
              isLoading={isFetching}
            >
              {(item: any) => (
                <TableRow key={item.id || item.email || item.date}>
                  {(columnKey) => (
                    <TableCell>{renderCell(data, item, columnKey)}</TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Table Pagination */}
        {!hidePagination && (
          <div className="table-striped-even mt-1	flex items-center text-sm sm:justify-end sm:text-base">
            <div className="ml-[15px] justify-start sm:ml-0 sm:ml-4 sm:mr-8">
              <label
                htmlFor="rowsPerPage"
                className="font-sans opacity-30 sm:mr-2"
              >
                Rows per page:
              </label>
              <select
                id="rowsPerPage"
                className="select-menu font-open-sans rounded border-none bg-transparent px-2 py-1 font-normal text-gray-500"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
              >
                {rowsPerPageOptions.map((option) => (
                  <option
                    key={option}
                    value={option}
                    className="font-open-sans text-gray-600 sm:font-normal"
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex">
              <div className="ml-[8px] mt-2 opacity-30 sm:ml-[45px] sm:mr-8">
                <p>{rowsRange}</p>
              </div>
              <button
                onClick={handleClickPrevPage}
                className="ml-[18px] mr-2 text-xs sm:ml-0 sm:mr-3"
              >
                <LeftArrowIcon />
              </button>
              <button onClick={handleClickNextPage} className="text-xs">
                <RightArrowIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
